import { formatNumber } from '../../lib/format.ts';
import { useLocale, useT } from '../../lib/i18n.tsx';
import { cn } from '../../lib/cn.ts';

export interface FunnelBar {
  milestone: string;
  label: string;
  count: number;
  /** Visit steps are optional: a customer can buy without visiting. */
  optional: boolean;
}

/**
 * Horizontal bars per funnel step, scaled to the first step. Purely a picture: the numbers and
 * percentages are in the table next to it, so the bars are hidden from assistive technology.
 */
export function FunnelBars({ steps }: { steps: readonly FunnelBar[] }) {
  const t = useT();
  const locale = useLocale();
  const max = Math.max(1, ...steps.map((s) => s.count));
  return (
    <div className="space-y-2" aria-hidden="true" data-testid="funnel-bars">
      {steps.map((step) => (
        <div
          key={step.milestone}
          className="grid grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-3 text-sm"
        >
          <span className="truncate">
            {step.label}
            {step.optional ? (
              <span className="ml-1 text-xs text-muted-foreground">({t.funnel.optionalStep})</span>
            ) : null}
          </span>
          <div className="h-5 rounded bg-muted">
            <div
              className={cn('h-5 rounded', step.optional ? 'bg-primary/60' : 'bg-primary')}
              style={{ width: `${Math.max(2, (step.count / max) * 100)}%` }}
            />
          </div>
          <span className="tabular-nums">{formatNumber(step.count, locale)}</span>
        </div>
      ))}
    </div>
  );
}
